/**
 * CORE-2001 — a browser that rejects a reconfiguration which changes nothing.
 *
 * Agora's web SDK (4.24.2 and 4.24.9 alike) builds the media connection inside
 * `P2PChannel.startP2PConnection` as `new RTCPeerConnection(cfg)` and on the
 * next line calls `setConfiguration(cfg)` with the very same `cfg`; when the
 * gateway socket opens it calls `setConfiguration` once more with the same TURN
 * entry ("use single turn, use turn server index: 0"). Neither call changes
 * anything. Chrome 155 on macOS — dev 14–29.10: four users, only that
 * version and OS, never on Windows 155 or on 156, never on prod — sometimes
 * answers both with `InvalidModificationError: Attempted to modify the
 * PeerConnection's configuration in an unsupported way`. The SDK does not
 * catch it: the request for the join message rejects, the join never
 * settles, our 20 s deadline fires, the proxied retry hits the same wall
 * (room 6685, 29.10: three joins in 42 s, the host never got in; GlitchTip 83/84).
 *
 * The connection already holds exactly what was asked for — it was constructed
 * with it — so the honest answer to "set X" on a connection that has X is
 * "done". This wrapper does that and nothing more: the real `setConfiguration`
 * always runs first; only an InvalidModificationError whose requested
 * configuration equals the connection's current one (ICE servers and transport
 * policy, plus any other member the caller named) is answered with success. A
 * real change that the browser refuses is thrown on as before — hiding that
 * would leave the SDK believing in TURN servers it doesn't have.
 *
 * Web only; a no-op where RTCPeerConnection doesn't exist. Installed once at
 * boot, before any SDK connection, and left in place like iceRoute.ts.
 */

const GUARD_MARK = '__agapeNoopReconfigGuard';

/**
 * Fired on the guarded window for every answered no-op, so RTC telemetry can
 * count them per session (liveTelemetry listens; this module imports nothing).
 * `detail` carries no secrets: counts and connection states only.
 */
export const NOOP_RECONFIG_EVENT = 'agape:rtc-noop-reconfig';

/** How many no-op reconfigurations were answered here (for diagnostics). */
let absorbed = 0;
export function absorbedNoopReconfigurations(): number {
  return absorbed;
}

type IceServerLike = { urls?: string | string[]; username?: string; credential?: unknown };

function normalizeServers(servers: unknown): string {
  const list = Array.isArray(servers) ? (servers as IceServerLike[]) : [];
  return JSON.stringify(
    list.map(s => ({
      urls: (Array.isArray(s?.urls) ? s.urls : s?.urls != null ? [s.urls] : []).map(String),
      username: s?.username ?? '',
      credential: s?.credential ?? '',
    })),
  );
}

/** Members whose omission means the spec default, so omitted ≠ "keep". */
const DEFAULTED: Record<string, unknown> = { iceTransportPolicy: 'all' };

/**
 * True when applying `requested` to a connection whose configuration is
 * `current` would change nothing. `credentialType` is ignored (Chrome no
 * longer reports it); certificates can't be compared, so naming them is
 * never a no-op.
 */
export function isNoopReconfiguration(requested: unknown, current: unknown): boolean {
  if (!requested || typeof requested !== 'object' || !current || typeof current !== 'object') return false;
  const req = requested as Record<string, unknown>;
  const cur = current as Record<string, unknown>;
  if ('certificates' in req && req.certificates !== undefined) return false;
  if (normalizeServers(req.iceServers) !== normalizeServers(cur.iceServers)) return false;
  const keys = new Set([...Object.keys(DEFAULTED), ...Object.keys(req)]);
  keys.delete('iceServers');
  keys.delete('certificates');
  for (const key of keys) {
    const want = req[key] !== undefined ? req[key] : DEFAULTED[key];
    if (want === undefined) continue;
    const have = cur[key] !== undefined ? cur[key] : DEFAULTED[key];
    if (want !== have) return false;
  }
  return true;
}

export function installNoopReconfigurationGuard(target: any = typeof window !== 'undefined' ? window : undefined): void {
  const proto = target?.RTCPeerConnection?.prototype;
  const original = proto?.setConfiguration;
  if (typeof original !== 'function' || original[GUARD_MARK]) return;
  const guarded = function setConfigurationNoopSafe(this: any, config?: RTCConfiguration) {
    try {
      return original.call(this, config);
    } catch (err) {
      if ((err as { name?: unknown } | null)?.name !== 'InvalidModificationError') throw err;
      let current: unknown;
      try {
        current = typeof this?.getConfiguration === 'function' ? this.getConfiguration() : undefined;
      } catch {
        throw err;
      }
      if (!isNoopReconfiguration(config, current)) throw err;
      absorbed += 1;
      // A console line becomes a Sentry breadcrumb: visible next to any later
      // failure of this join, without filing the browser's refusal as an error.
      console.warn('[rtc] setConfiguration refused a configuration identical to the current one; treated as applied (CORE-2001)');
      try {
        const Ctor = target?.CustomEvent ?? (typeof CustomEvent !== 'undefined' ? CustomEvent : undefined);
        if (Ctor && typeof target?.dispatchEvent === 'function') {
          target.dispatchEvent(new Ctor(NOOP_RECONFIG_EVENT, {
            detail: {
              absorbedTotal: absorbed,
              iceServerCount: Array.isArray(config?.iceServers) ? config!.iceServers!.length : 0,
              iceTransportPolicy: config?.iceTransportPolicy ?? 'all',
              connectionState: this?.connectionState ?? null,
              signalingState: this?.signalingState ?? null,
              iceGatheringState: this?.iceGatheringState ?? null,
            },
          }));
        }
      } catch { /* telemetry must never break the join */ }
      return undefined;
    }
  } as any;
  guarded[GUARD_MARK] = true;
  proto.setConfiguration = guarded;
}
